/**
 * Bearer-token session.
 *
 * Today: opaque 43-char `api_key` returned ONCE by `POST /users`. UI flow:
 *   1. First load → user pastes their api_key into a one-shot screen.
 *   2. The key is persisted under `te:session:v1` in localStorage.
 *   3. The api client reads it via `getAccessToken()` and sends it as
 *      `Authorization: Bearer <key>` on every request.
 *   4. On 401 the api client calls `signOut()` and the UI redirects to
 *      the paste screen.
 *
 * Tomorrow (Tier B6): same `Authorization: Bearer <jwt>` wire shape; the
 * api_key bucket is replaced by `accessToken` + `refreshToken` (separate
 * storage), and a 401 triggers refresh before the redirect. The component
 * surface in app/auth/* changes from "paste" to "login form" without
 * touching the api client.
 *
 * Multi-tab: a `BroadcastChannel` propagates sign-in / sign-out / kill-
 * switch across all tabs so an operator can't be partially-logged-in.
 */

'use client';

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

import type { Operator, OperatorRole } from '@/types/domain';

/* ============================================================================
 * Types
 * ============================================================================ */

export type AuthKind = 'api_key' | 'jwt' | 'none';

export interface SessionState {
  kind: AuthKind;
  /** Opaque token (api_key or JWT access token). */
  accessToken: string | null;
  /** Future: JWT refresh token. Always null under api_key. */
  refreshToken: string | null;
  /** Hydrated `/users/me` (or mock /operators/me) — what every component reads from. */
  me: Operator | null;
  /** Last token-validation timestamp (ms). */
  lastValidatedAt: number | null;
  /** UTC ISO-8601 of token expiry; null for opaque api_keys. */
  expiresAt: string | null;
}

interface SessionActions {
  signInWithApiKey: (apiKey: string, me: Operator) => void;
  signInWithJwt: (accessToken: string, refreshToken: string, me: Operator, expiresAt: string) => void;
  setMe: (me: Operator) => void;
  signOut: (reason?: 'user' | 'expired' | 'kill_switch' | '401') => void;
  setLastValidatedAt: (ms: number) => void;
  /** Test/dev: install a fake operator without going through paste / login. */
  __devImpersonate: (operator: Operator, accessToken?: string) => void;
}

type Store = SessionState & SessionActions & {
  /** True once the persist middleware has rehydrated from localStorage.
   * Route guards MUST wait for this before redirecting, otherwise a full
   * page reload sees the pre-hydration empty state and bounces to sign-in. */
  hasHydrated: boolean;
  _setHydrated: () => void;
};

/* ============================================================================
 * BroadcastChannel
 * ============================================================================ */

const CHANNEL_NAME = 'te:auth';
let bc: BroadcastChannel | null = null;
const getChannel = (): BroadcastChannel | null => {
  if (typeof window === 'undefined' || typeof BroadcastChannel === 'undefined') {
    return null;
  }
  if (bc === null) {
    bc = new BroadcastChannel(CHANNEL_NAME);
  }
  return bc;
};

const broadcast = (msg: { kind: 'signed_in' | 'signed_out' | 'kill_switch'; reason?: string }): void => {
  const channel = getChannel();
  if (channel !== null) {
    try {
      channel.postMessage(msg);
    } catch {
      /* non-clonable payload — ignore */
    }
  }
};

/* ============================================================================
 * Store
 * ============================================================================ */

const empty: SessionState = {
  kind: 'none',
  accessToken: null,
  refreshToken: null,
  me: null,
  lastValidatedAt: null,
  expiresAt: null,
};

export const useSessionStore = create<Store>()(
  persist(
    (set) => ({
      ...empty,
      // Not persisted (see partialize); set by onRehydrateStorage after load.
      hasHydrated: false,
      _setHydrated: () => set({ hasHydrated: true }),
      signInWithApiKey: (apiKey, me) => {
        set({
          kind: 'api_key',
          accessToken: apiKey,
          refreshToken: null,
          me,
          lastValidatedAt: Date.now(),
          expiresAt: null,
        });
        broadcast({ kind: 'signed_in' });
      },
      signInWithJwt: (accessToken, refreshToken, me, expiresAt) => {
        set({
          kind: 'jwt',
          accessToken,
          refreshToken,
          me,
          lastValidatedAt: Date.now(),
          expiresAt,
        });
        broadcast({ kind: 'signed_in' });
      },
      setMe: (me) => set({ me }),
      signOut: (reason = 'user') => {
        set(empty);
        broadcast({ kind: 'signed_out', reason });
      },
      setLastValidatedAt: (ms) => set({ lastValidatedAt: ms }),
      __devImpersonate: (operator, accessToken = 'dev-impersonation-token') => {
        set({
          kind: 'api_key',
          accessToken,
          refreshToken: null,
          me: operator,
          lastValidatedAt: Date.now(),
          expiresAt: null,
        });
        broadcast({ kind: 'signed_in' });
      },
    }),
    {
      name: 'te:session:v1',
      version: 1,
      storage: createJSONStorage(() => {
        if (typeof window === 'undefined') {
          const data = new Map<string, string>();
          return {
            getItem: (k: string): string | null => data.get(k) ?? null,
            setItem: (k: string, v: string): void => {
              data.set(k, v);
            },
            removeItem: (k: string): void => {
              data.delete(k);
            },
          };
        }
        return localStorage;
      }),
      // Only persist the real session fields — never the transient hydration
      // flag or the action functions.
      partialize: (s) => ({
        kind: s.kind,
        accessToken: s.accessToken,
        refreshToken: s.refreshToken,
        me: s.me,
        lastValidatedAt: s.lastValidatedAt,
        expiresAt: s.expiresAt,
      }),
      // Fires after rehydration completes (even when storage is empty), so the
      // guard knows the real auth state is now loaded.
      onRehydrateStorage: () => (state) => {
        state?._setHydrated();
      },
    },
  ),
);

/* ============================================================================
 * Convenience accessors — synchronous reads off the store, used by the api
 * client and the route guards. NOT React hooks; safe to call anywhere.
 * ============================================================================ */

export const getAccessToken = (): string | null => useSessionStore.getState().accessToken;
export const getMe = (): Operator | null => useSessionStore.getState().me;
export const getMyRole = (): OperatorRole | null => useSessionStore.getState().me?.role ?? null;
export const isAuthenticated = (): boolean => useSessionStore.getState().accessToken !== null;

/* ============================================================================
 * Auth header helper — single place that knows the wire shape.
 * ============================================================================ */

export const authHeader = (): Record<string, string> => {
  const token = getAccessToken();
  return token === null ? {} : { Authorization: `Bearer ${token}` };
};

/* ============================================================================
 * Multi-tab listener
 * ============================================================================ */

const install = (): void => {
  const channel = getChannel();
  if (channel === null) {
    return;
  }
  channel.onmessage = (msg: MessageEvent<unknown>) => {
    const data = msg.data as { kind?: string; reason?: string };
    if (data.kind === 'signed_out') {
      // Reset local copy without firing another broadcast (avoid ping-pong).
      useSessionStore.setState(empty);
    }
  };
};

install();
